import { createSelector } from 'reselect';
import { selectIsSibelgaOperation } from './operationSelectors';
import moment from 'moment';

const selectBillingEntities = (state) => state.billingNew.billingEntities || [];
const selectBillableEntities = (state) =>
    state.billingNew.billableEntities || [];
const selectInvoices = (state) => state.billingNew.invoices || [];
export const selectLoadingInvoices = (state) =>
    state.billingNew.loadingInvoices;
const selectLastInvoiceDate = (state) => state.billingNew.lastInvoiceDate;
const selectBillingPlans = (state) => state.billingNew.billingPlans;
export const selectArrayBillingPlans = (state) =>
    state.billingNew.billingPlans || [];
const selectBillingEntityLogo = (state) => state.billingNew.billingEntityLogo;
const selectBillingSubscriptions = (state) => {
    return state.billingNew.billingSubscriptions || [];
};

const SibelgaContractType = {
    consumption: 'CONSUMPTION',
    production: 'PRODUCTION',
};

export const selectNonZeroInvoices = createSelector(
    selectInvoices,
    (invoices) => invoices.filter((invoice) => invoice.status !== 'NOT_CHARGED')
);

const selectDraftInvoicesSortedByEndDate = createSelector(
    selectInvoices,
    (invoices) => {
        const draftInvoices = invoices.filter(
            (invoice) => invoice.status === 'DRAFT'
        );
        return draftInvoices.sort((a, b) => {
            return moment(a.periodEnd).isAfter(moment(b.periodEnd)) ? 1 : -1;
        });
    }
);

const selectFinalizedInvoicesSortedByEndDate = createSelector(
    selectInvoices,
    (invoices) => {
        const finalizedInvoices = invoices.filter(
            (invoice) => invoice.status === 'FINALIZED'
        );
        return finalizedInvoices.sort((a, b) => {
            return moment(a.periodEnd).isAfter(moment(b.periodEnd)) ? 1 : -1;
        });
    }
);

const selectNotChargedInvoicesSortedByEndDate = createSelector(
    selectInvoices,
    (invoices) => {
        const notChargedInvoices = invoices.filter(
            (invoice) => invoice.status === 'NOT_CHARGED'
        );
        return notChargedInvoices.sort((a, b) => {
            return moment(a.periodEnd).isAfter(moment(b.periodEnd)) ? 1 : -1;
        });
    }
);

export const selectShowNotChargedInvoicesToggle = createSelector(
    selectNotChargedInvoicesSortedByEndDate,
    (invoices) => invoices.length > 0
);

const selectDraftAndFinalizedInvoices = createSelector(
    selectDraftInvoicesSortedByEndDate,
    selectFinalizedInvoicesSortedByEndDate,
    (draft, finalized) => [...draft, ...finalized]
);

const selectNotChargedAndDraftAndFinalizedInvoices = createSelector(
    selectNotChargedInvoicesSortedByEndDate,
    selectDraftAndFinalizedInvoices,
    (notCharged, draftAndFinalized) => [...notCharged, ...draftAndFinalized]
);

const selectDraftAndFinalizedInvoicesGroupedByEndDateMonth = createSelector(
    selectDraftAndFinalizedInvoices,
    (invoices) => groupInvoicesByMonth(invoices)
);

const selectNotChargedAndDraftAndFinalizedInvoicesGroupedByEndDateMonth =
    createSelector(selectNotChargedAndDraftAndFinalizedInvoices, (invoices) =>
        groupInvoicesByMonth(invoices)
    );

const selectSibelgaDraftAndFinalizedConsumptionInvoices = createSelector(
    selectDraftAndFinalizedInvoices,
    (invoices) =>
        invoices.filter(
            (invoice) => invoice.billingEntity.producerIds.length === 0
        )
);

const selectSibelgaNotChargedAndDraftAndFinalizedConsumptionInvoices =
    createSelector(selectNotChargedAndDraftAndFinalizedInvoices, (invoices) =>
        invoices.filter(
            (invoice) => invoice.billingEntity.producerIds.length === 0
        )
    );

const selectSibelgaDraftAndFinalizedProductionInvoices = createSelector(
    selectDraftAndFinalizedInvoices,
    (invoices) =>
        invoices.filter(
            (invoice) => invoice.billableEntity.consumerIds.length === 0
        )
);

const selectSibelgaNotChargedAndDraftAndFinalizedProductionInvoices =
    createSelector(selectNotChargedAndDraftAndFinalizedInvoices, (invoices) =>
        invoices.filter(
            (invoice) => invoice.billableEntity.consumerIds.length === 0
        )
    );

const selectDraftAndFinalizedSibelgaConsumptionInvoicesGroupedByEndDateMonth =
    createSelector(
        selectSibelgaDraftAndFinalizedConsumptionInvoices,
        (invoices) => {
            const groupedInvoicesByMonth = groupInvoicesByMonth(invoices);
            const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
                ([date1], [date2]) =>
                    moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                        ? 1
                        : -1
            );
            return sortedGroups;
        }
    );

const selectNotChargedAndDraftAndFinalizedSibelgaConsumptionInvoicesGroupedByEndDateMonth =
    createSelector(
        selectSibelgaNotChargedAndDraftAndFinalizedConsumptionInvoices,
        (invoices) => {
            const groupedInvoicesByMonth = groupInvoicesByMonth(invoices);
            const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
                ([date1], [date2]) =>
                    moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                        ? 1
                        : -1
            );
            return sortedGroups;
        }
    );

const selectDraftAndFinalizedSibelgaProductionInvoicesGroupedByEndDateMonth =
    createSelector(
        selectSibelgaDraftAndFinalizedProductionInvoices,
        (invoices) => {
            const groupedInvoicesByMonth = groupInvoicesByMonth(invoices);
            const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
                ([date1], [date2]) =>
                    moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                        ? 1
                        : -1
            );
            return sortedGroups;
        }
    );

const selectNotChargedAndDraftAndFinalizedSibelgaProductionInvoicesGroupedByEndDateMonth =
    createSelector(
        selectSibelgaNotChargedAndDraftAndFinalizedProductionInvoices,
        (invoices) => {
            const groupedInvoicesByMonth = groupInvoicesByMonth(invoices);
            const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
                ([date1], [date2]) =>
                    moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                        ? 1
                        : -1
            );
            return sortedGroups;
        }
    );

const groupInvoicesByMonth = (invoices) => {
    return invoices.reduce((acc, currentInvoice) => {
        const periodEnd = currentInvoice.periodEnd;
        const dayBefore = moment(periodEnd).subtract(1, 'days');
        const endDate = `${dayBefore.year()}-${dayBefore.month() + 1}`; // Format as YYYY-MM
        if (!acc[endDate]) {
            acc[endDate] = [];
        }
        acc[endDate].push(currentInvoice);
        return acc;
    }, {});
};

const filterInvoices = (
    invoices,
    billingEntityId,
    billableEntityId,
    isSibelga,
    sibelgaContractType
) => {
    let billingEntityIdToUse = billingEntityId;
    let billableEntityIdToUse = billableEntityId;
    if (isSibelga) {
        const consumptionContract =
            sibelgaContractType === SibelgaContractType.consumption;
        billingEntityIdToUse = !consumptionContract ? billingEntityId : null;
        billableEntityIdToUse = consumptionContract ? billableEntityId : null;
    }
    const billingEntityInvoices = invoices.filter(
        (invoice) =>
            !billingEntityIdToUse ||
            invoice.billingEntity?.id === billingEntityIdToUse
    );
    return billingEntityInvoices.filter(
        (invoice) =>
            !billableEntityIdToUse ||
            invoice.billableEntity?.id === billableEntityIdToUse
    );
};

export const makeSelectHasDebitInvoices = (billingEntityId) =>
    createSelector(
        selectDraftAndFinalizedInvoicesGroupedByEndDateMonth,
        (invoicesGroupedByMonths) => {
            if (!billingEntityId) {
                return {};
            }
            const hasDebitInvoicesByPeriod = {};
            Object.entries(invoicesGroupedByMonths).forEach(
                ([month, invoices]) => {
                    const hasDebitInvoices = invoices.some(
                        (invoice) =>
                            invoice.billingEntity.id === billingEntityId &&
                            invoice.paymentMethod === 'DEBIT'
                    );
                    hasDebitInvoicesByPeriod[month] = hasDebitInvoices;
                }
            );
            return hasDebitInvoicesByPeriod;
        }
    );

export const makeSelectInvoicesByMonthAndBillingEntity = (
    month,
    billingEntityId,
    withNotCharged = false
) =>
    createSelector(
        withNotCharged
            ? selectNotChargedAndDraftAndFinalizedInvoicesGroupedByEndDateMonth
            : selectDraftAndFinalizedInvoicesGroupedByEndDateMonth,
        (invoicesGroupedByMonths) => {
            const invoices = invoicesGroupedByMonths[month].filter(
                (invoice) => invoice.billingEntity.id === billingEntityId
            );
            return invoices;
        }
    );

export const makeSelectDebitInvoicesByMonthAndBillingEntity = (
    month,
    billingEntityId
) =>
    createSelector(
        makeSelectInvoicesByMonthAndBillingEntity(
            month,
            billingEntityId,
            false
        ),
        (invoices) =>
            invoices.filter((invoice) => invoice.paymentMethod === 'DEBIT')
    );

export const makeSelectDisplayedInvoices = (
    isSibelga,
    billingEntityId,
    billableEntityId,
    sibelgaContractType,
    withNotCharged
) =>
    createSelector(
        makeSelectInvoicesGroupedAndSortedByMonth(
            isSibelga,
            withNotCharged,
            sibelgaContractType
        ),
        (invoicesGroupedByMonth) => {
            const filteredInvoicesByMonth = [];
            invoicesGroupedByMonth.forEach(([date, invoices]) => {
                const filteredInvoices = {
                    date,
                    invoices: filterInvoices(
                        invoices,
                        billingEntityId,
                        billableEntityId,
                        isSibelga,
                        sibelgaContractType
                    ),
                };
                if (filteredInvoices.invoices.length) {
                    filteredInvoicesByMonth.push(filteredInvoices);
                }
            });
            return filteredInvoicesByMonth;
        }
    );

const selectDraftAndFinalizedInvoicesGroupedAndSortedByMonth = createSelector(
    selectDraftAndFinalizedInvoicesGroupedByEndDateMonth,
    (groupedInvoicesByMonth) => {
        const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
            ([date1], [date2]) =>
                moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                    ? 1
                    : -1
        );
        return sortedGroups;
    }
);

const selectNotChargedAndDraftAndFinalizedInvoicesGroupedAndSortedByMonth =
    createSelector(
        selectNotChargedAndDraftAndFinalizedInvoicesGroupedByEndDateMonth,
        (groupedInvoicesByMonth) => {
            const sortedGroups = Object.entries(groupedInvoicesByMonth).sort(
                ([date1], [date2]) =>
                    moment(date1, 'YYYY-MM').isBefore(moment(date2, 'YYYY-MM'))
                        ? 1
                        : -1
            );
            return sortedGroups;
        }
    );
export const selectHasOnlyNotChargedInvoices = createSelector(
    selectNotChargedAndDraftAndFinalizedInvoicesGroupedAndSortedByMonth,
    (invoicesGroupedByMonths) => {
        let hasAllNotCharged = {};
        invoicesGroupedByMonths.forEach(([monthKey, invoices]) => {
            const allNotCharged = !invoices.some(
                (i) => i.status !== 'NOT_CHARGED'
            );
            hasAllNotCharged[monthKey] = allNotCharged;
        });
        return hasAllNotCharged;
    }
);
const makeSelectInvoicesGroupedAndSortedByMonth = (
    isSibelga,
    withNotCharged,
    sibelgaContractType = null
) => {
    if (!isSibelga) {
        return withNotCharged
            ? selectNotChargedAndDraftAndFinalizedInvoicesGroupedAndSortedByMonth
            : selectDraftAndFinalizedInvoicesGroupedAndSortedByMonth;
    }
    if (sibelgaContractType === SibelgaContractType.consumption) {
        return withNotCharged
            ? selectNotChargedAndDraftAndFinalizedSibelgaConsumptionInvoicesGroupedByEndDateMonth
            : selectDraftAndFinalizedSibelgaConsumptionInvoicesGroupedByEndDateMonth;
    }
    return withNotCharged
        ? selectNotChargedAndDraftAndFinalizedSibelgaProductionInvoicesGroupedByEndDateMonth
        : selectDraftAndFinalizedSibelgaProductionInvoicesGroupedByEndDateMonth;
};

export const selectNotCancelledInvoices = createSelector(
    selectInvoices,
    (invoices) => invoices.filter((invoice) => invoice.status !== 'CANCELLED')
);

const selectCancelledInvoices = createSelector(selectInvoices, (invoices) =>
    invoices.filter((invoice) => invoice.status === 'CANCELLED')
);

const selectSibelgaCancelledConsumptionInvoices = createSelector(
    selectCancelledInvoices,
    (invoices) =>
        invoices.filter(
            (invoice) => invoice.billingEntity.producerIds.length === 0
        )
);

const selectSibelgaCancelledProductionInvoices = createSelector(
    selectCancelledInvoices,
    (invoices) =>
        invoices.filter(
            (invoice) => invoice.billableEntity.consumerIds.length === 0
        )
);

const makeSelectSibelgaCancelledInvoices = (sibelgaContractType) =>
    createSelector(
        selectSibelgaCancelledConsumptionInvoices,
        selectSibelgaCancelledProductionInvoices,
        (consumptionInvoices, productionInvoices) => {
            if (sibelgaContractType === SibelgaContractType.consumption) {
                return consumptionInvoices;
            }
            return productionInvoices;
        }
    );

export const makeSelectCancelledInvoices = (
    isSibelga,
    billingEntityId,
    billableEntityId,
    sibelgaContractType
) =>
    createSelector(
        !isSibelga
            ? selectCancelledInvoices
            : makeSelectSibelgaCancelledInvoices(sibelgaContractType),
        (cancelledInvoices) => {
            return filterInvoices(
                cancelledInvoices,
                billingEntityId,
                billableEntityId,
                isSibelga,
                sibelgaContractType
            );
        }
    );

export const selectProducersBillingEntities = createSelector(
    [selectBillingEntities, selectIsSibelgaOperation],
    (billingEntities, isSibelga) =>
        isSibelga
            ? billingEntities.filter((be) => be.producerIds.length > 0)
            : billingEntities
);

export const selectConsumersBillableEntities = createSelector(
    [selectBillableEntities, selectIsSibelgaOperation],
    (billableEntities, isSibelga) =>
        isSibelga
            ? billableEntities.filter((be) => be.consumerIds.length > 0)
            : billableEntities
);

export const selectSortedBillingSubscriptions = createSelector(
    selectBillingSubscriptions,
    (billingSubscriptions) => {
        const sortedSubscriptions = [...billingSubscriptions];
        sortedSubscriptions.sort(
            ({ startDate: startDate1 }, { startDate: startDate2 }) =>
                moment(startDate1).isAfter(startDate2) ? 1 : -1
        );
        return sortedSubscriptions;
    }
);

export const makeSelectConsumerSubscriptions = (consumerId) =>
    createSelector(selectBillingSubscriptions, (billingSubscriptions) =>
        billingSubscriptions.filter(
            (subscription) => subscription.consumerId === consumerId
        )
    );

export const makeSelectProducerSubscriptions = (producerId) =>
    createSelector(selectBillingSubscriptions, (billingSubscriptions) =>
        billingSubscriptions.filter(
            (subscription) => subscription.producerId === producerId
        )
    );

const selectIuBillingEntity = createSelector(
    selectBillingEntities,
    (billingEntities) =>
        billingEntities?.find((entity) => entity.producerIds.length === 0)
);
const selectIuBillableEntity = createSelector(
    selectBillableEntities,
    (billableEntities) =>
        billableEntities?.find((entity) => entity.consumerIds.length === 0)
);
const selectInvoiceGenerationProgress = (state) =>
    state.billingNew.invoiceGenerationProgress;

const makeSelectBillableEntity = (billableEntityId) =>
    createSelector(
        selectBillableEntities,
        (billableEntities) =>
            billableEntities?.find(
                (entity) => entity.id === billableEntityId
            ) || null
    );

const makeSelectBillingEntity = (billingEntityId) =>
    createSelector(
        selectBillingEntities,
        (billingEntities) =>
            billingEntities?.find((entity) => entity.id === billingEntityId) ||
            null
    );

const selectEditOption = (state) => state.billingNew.entityEditOption;

const selectBillRun = (state) => state.billingNew.billRun || {};

export const selectBillRunProgress = createSelector(
    selectBillRun,
    (billRun) => billRun.progress || {}
);

export const selectBillRunExecutionTime = createSelector(
    selectBillRun,
    (billRun) =>
        billRun.createdAt
            ? moment(billRun.createdAt).format('DD MMMM YYYY à HH:mm')
            : '-'
);

const selectSortedBillRunInvoices = createSelector(selectBillRun, (billRun) =>
    billRun.invoices
        ? billRun.invoices
              .filter((invoice) =>
                  ['SUCCESS', 'FAILURE'].includes(invoice.status)
              )
              .sort((inv1) => (inv1.status === 'FAILURE' ? -1 : 1))
        : null
);

export const makeSelectBillRunInvoices = (
    status,
    billingEntity,
    billableEntity
) =>
    createSelector(selectSortedBillRunInvoices, (invoices) =>
        invoices
            ? invoices.filter((invoice) => {
                  if (status && invoice.status !== status) return false;
                  if (
                      billingEntity &&
                      invoice.billingEntity.id !== billingEntity
                  )
                      return false;
                  if (
                      billableEntity &&
                      invoice.billableEntity.id !== billableEntity
                  )
                      return false;
                  return true;
              })
            : null
    );

export {
    selectBillingEntities,
    selectBillableEntities,
    selectInvoices,
    selectLastInvoiceDate,
    selectBillingPlans,
    selectBillingSubscriptions,
    selectIuBillingEntity,
    selectIuBillableEntity,
    selectBillingEntityLogo,
    selectInvoiceGenerationProgress,
    selectEditOption,
    makeSelectBillableEntity,
    makeSelectBillingEntity,
};
